import { AutocompleteState, BaseItem } from "@algolia/autocomplete-core";
import classnames from "classnames";
import { isEmpty } from "lodash-es";
import React, { MouseEvent, MutableRefObject } from "react";

import { KeyNames } from "@utils/enums";
import { isDefined } from "@utils/helpers";
import { createKeyHandler } from "@utils/keyboard";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { AutocompleteActions } from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/useAutocompleteApi.hook";
import { ResultsPanelComponent } from "@components/Search/SearchAutocomplete/ResultsPanel/resultsPanel.component";
import { getResultsPanelBlocks } from "@components/Search/SearchAutocomplete/ResultsPanel/resultsPanel.templates";

import { AutocompleteRef } from "../AutocompleteSearchBox/autocompleteSearchBox.container";
import { getLastAutocompleteItem } from "../helpers/accessibility.helpers";
import "./resultsPanel.scss";

type ResultsPanelProps = {
    onMouseDown: (event: MouseEvent) => void;
    items?: Record<string, BaseItem[]>;
    autocompleteState?: AutocompleteState<BaseItem>;
    autocompleteActions?: AutocompleteActions;
    autocompleteRef: MutableRefObject<AutocompleteRef>;
};

const ResultsPanel = ({
    onMouseDown,
    items,
    autocompleteState,
    autocompleteActions,
    autocompleteRef,
}: ResultsPanelProps) => {
    const { t } = useGranite();

    if (
        !isDefined(items) ||
        !isDefined(autocompleteState) ||
        !isDefined(autocompleteActions)
    ) {
        return null;
    }

    const blocks = getResultsPanelBlocks(
        items,
        autocompleteState,
        autocompleteActions,
    );

    const hasResults =
        !!Object.entries(items).length &&
        Object.values(items).some(resultList => resultList.length > 0);

    const noResults =
        isEmpty(autocompleteState?.query) || hasResults
            ? null
            : t("searchBox.panel.noResults");

    const handleResultsPanelKeyDown = createKeyHandler([
        {
            keys: [KeyNames.TAB],
            handler: event => {
                if (event.shiftKey) return;

                const lastAutocompleteItem = getLastAutocompleteItem();

                if (
                    isDefined(lastAutocompleteItem) &&
                    event.target === lastAutocompleteItem
                ) {
                    event.preventDefault();
                    autocompleteRef.current.input?.focus();
                }
            },
        },
        {
            keys: [KeyNames.ENTER],
            handler: () => {
                const focussedElement = document.activeElement;
                if (!(focussedElement instanceof HTMLAnchorElement)) return;

                focussedElement.click();
            },
        },
    ]);

    return (
        <div
            className={classnames("resultsPanel")}
            onMouseDown={onMouseDown}
            onKeyDown={handleResultsPanelKeyDown}
        >
            <ResultsPanelComponent blocks={blocks} noResults={noResults} />
        </div>
    );
};

export { ResultsPanel };
